import type {FC} from 'react'
import {memo, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Box, Button, CircularProgress, Typography} from '@mui/material'
import {useListMessagesQuery} from '@apis/aiCoreAPI.ts'
import {useConversationEvents} from './ConversationStreamProvider'
import ChatSkeleton from './chatMessageList/ChatSkeleton'
import type {Message, ToolSummaryContent} from '@interfaces/alpinist/message'
import type {WSEvent} from '@interfaces/alpinist/ws'
import type {UserContextProps} from '@containers/App/UserProvider'
import {useUserContext} from '@containers/App/UserProvider'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import type {IndexingState} from './chatMessageList/indexingNotice'
import {nextIndexingDocuments} from './chatMessageList/indexingNotice'
import {IndexingProvider} from './chatMessageList/IndexingContext'
import DocumentPreviewModal from '@shared/components/DocumentPreviewModal'
import ToolBubble from './messages/ToolBubble'
import WaitingBubble from './messages/WaitingBubble'
import AgentBubble from './messages/AgentBubble'
import MarkdownText from './messages/markdown/MarkdownText'
import ChatMessageRow from './chatMessageList/ChatMessageRow'
import ImageThumbnail from './messages/ImageThumbnail'
import {isImageAttachment} from '@containers/Dashboards/AgentChat/attachments'
import {
  clearLiveCaptures,
  settleLiveCaptures,
  usePendingCaptures,
} from '@shared/alpinist/screenCapture/liveCaptures'
import {stripReasoningText} from './chatMessageList/stripReasoningText'
import {useActorLabels} from './chatMessageList/useActorLabels'
import {MESSAGE_PAGE_SIZE} from './chatMessageList/constants'
import {useConversationRun} from './conversationRun/ConversationRunProvider'

interface KbPreviewState {
  documentId: string
  title?: string
  page?: number
}

const TOP_ROOT_MARGIN = '200px 0px 0px 0px'
const NEAR_BOTTOM_PX = 120

interface ChatMessageListProps {
  conversationId: string
  emptyStateText?: string
  tone?: 'default' | 'incident'
  /** Re-send the last user turn (used by the Retry button on an error row). */
  onResend?: (text: string) => Promise<void> | void
  sending?: boolean
}

const ChatMessageList: FC<ChatMessageListProps> = ({
  conversationId,
  emptyStateText,
  tone = 'default',
  onResend,
  sending = false,
}) => {
  const [t] = useCustomTranslation('alpinist')
  // Tolerate a missing UserProvider (e.g. the public Alpinist demo embed renders a minimal
  // provider stack). Sibling chart components guard the same way.
  const {currentUser} = (useUserContext() as UserContextProps | null) ?? {currentUser: null}
  const showAuthors = tone === 'incident'

  const scrollRef = useRef<HTMLDivElement | null>(null)
  const topRef = useRef<HTMLDivElement | null>(null)

  const [kbPreview, setKbPreview] = useState<KbPreviewState | null>(null)
  const [offset, setOffset] = useState(0)
  const [totalCount, setTotalCount] = useState<number | null>(null)
  const [messages, setMessages] = useState<Message[]>([])
  const [liveAgentText, setLiveAgentText] = useState('')
  const [liveToolUpdate, setLiveToolUpdate] = useState<ToolSummaryContent | null>(null)
  // Documents this turn is waiting to finish indexing (AB#5992). A file over
  // the sync-parse threshold is read by a worker and a 5 MB workbook takes
  // minutes, so without saying so the turn looks hung — and before the wait
  // existed the agent simply answered that it could not read the file. The
  // state is shown on the file's own card, which is where the user is already
  // looking.
  const [indexingDocuments, setIndexingDocuments] = useState<IndexingState>({})

  const bufferRef = useRef('')
  const rafRef = useRef<number | null>(null)
  const isFlushingRef = useRef(false)
  const atBottomRef = useRef(true)
  const forceScrollToBottomRef = useRef(false)
  const loadingOlderRef = useRef(false)

  const {getActorLabel, isActorLoading, resetActorLabels} = useActorLabels({
    messages,
    showAuthors,
    currentUser,
  })

  const onScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    atBottomRef.current = distanceFromBottom < NEAR_BOTTOM_PX
  }, [])

  // currentData, not data: data keeps the previous conversation's page until the
  // next one arrives, which put the old messages under the new conversation and
  // left a switch without any loading state.
  const {currentData, isFetching, isError, refetch} = useListMessagesQuery(
    {conversationId, limit: MESSAGE_PAGE_SIZE, offset},
    {
      skip: !conversationId,
      refetchOnFocus: true,
      refetchOnReconnect: true,
      refetchOnMountOrArgChange: true,
    },
  )

  const fetchedMessages = useMemo(() => currentData?.results ?? [], [currentData?.results])
  const count = currentData?.count
  // The first page of this conversation is not here yet (opening it, or
  // switching to it); later refetches of the same page keep what is shown.
  const loadingFirstPage = !!conversationId && offset === 0 && !currentData && !isError

  const stopFlush = useCallback(() => {
    if (rafRef.current != null) {
      cancelAnimationFrame(rafRef.current)
    }
    rafRef.current = null
    isFlushingRef.current = false
  }, [])

  const flushLiveText = useCallback(() => {
    if (!isFlushingRef.current) return
    setLiveAgentText(stripReasoningText(bufferRef.current, true))
    stopFlush()
  }, [stopFlush])

  const queueFlush = useCallback(() => {
    if (isFlushingRef.current) return
    isFlushingRef.current = true
    rafRef.current = requestAnimationFrame(flushLiveText)
  }, [flushLiveText])

  // Scroll only the list itself: scrollIntoView would also scroll the window and every other
  // scrollable ancestor, shifting the page around the chat (AB#6666).
  const scrollListToBottom = useCallback(() => {
    const el = scrollRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [])

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(scrollListToBottom)
  }, [scrollListToBottom])

  useEffect(() => {
    setOffset(0)
    setTotalCount(null)
    setMessages([])
    setLiveAgentText('')
    setLiveToolUpdate(null)
    // A wait runs for minutes; without this it followed the user into whatever
    // conversation they opened next and claimed a file was being read there.
    setIndexingDocuments({})
    // The citation modal is not modal over the sidebar, so switching threads
    // behind it left it open on the previous thread's document.
    setKbPreview(null)
    resetActorLabels()

    bufferRef.current = ''
    stopFlush()
  }, [conversationId, stopFlush, resetActorLabels])

  useEffect((): (() => void) => {
    return (): void => {
      stopFlush()
    }
  }, [stopFlush])

  useEffect(() => {
    if (!conversationId) return
    if (count != null) setTotalCount(count)

    if (offset === 0) {
      setMessages(fetchedMessages as Message[])
      scrollToBottom()
      return
    }

    setMessages((prev) => {
      const seen = new Set(prev.map((message) => message.id))
      const older = (fetchedMessages as Message[]).filter((message) => !seen.has(message.id))
      return [...prev, ...older]
    })
  }, [conversationId, offset, fetchedMessages, count, scrollToBottom])

  const hasMore = useMemo(() => {
    if (totalCount == null) return false
    return messages.length < totalCount
  }, [messages.length, totalCount])

  const loadOlder = useCallback(() => {
    if (!hasMore || isFetching || loadingOlderRef.current) return
    loadingOlderRef.current = true
    setOffset((currentOffset) => currentOffset + MESSAGE_PAGE_SIZE)
  }, [hasMore, isFetching])

  useEffect(() => {
    if (!isFetching) loadingOlderRef.current = false
  }, [isFetching])

  useEffect((): (() => void) | void => {
    const root = scrollRef.current
    const target = topRef.current
    if (!root || !target) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadOlder()
        }
      },
      {root, rootMargin: TOP_ROOT_MARGIN, threshold: 0},
    )

    observer.observe(target)
    return (): void => {
      observer.disconnect()
    }
  }, [loadOlder])

  const {subscribe} = useConversationEvents()
  const {deliveryOf} = useConversationRun()
  const pendingCaptures = usePendingCaptures(conversationId)
  // Revoke the object URLs of this conversation's screen captures when the
  // chat leaves it; the history then shows the persisted attachments.
  const answerIdsRef = useRef<string[]>([])
  answerIdsRef.current = messages.filter((m) => m.role === 'agent').map((m) => String(m.id))
  useEffect(() => () => clearLiveCaptures(conversationId, answerIdsRef.current), [conversationId])
  useEffect(() => {
    if (!subscribe) return

    const off = subscribe({
      onMessage: (event: WSEvent) => {
        // The socket is per conversation, but it is re-pointed rather than torn
        // down, so a frame for the conversation just left can still land here.
        // The reading state is the one thing that outlives a single turn, so it
        // is the one that shows up in the wrong thread.
        if (event.conversation_id && event.conversation_id !== conversationId) return

        setIndexingDocuments((current) => nextIndexingDocuments(current, event))

        switch (event.kind) {
          case 'agent.token': {
            const token = event.payload.token
            if (!token) return

            bufferRef.current += token
            queueFlush()
            if (atBottomRef.current) scrollToBottom()
            return
          }

          case 'msg.final': {
            const nextMessage = event.payload as Message

            if (nextMessage.role === 'user') {
              forceScrollToBottomRef.current = true
            }

            setMessages((prev) => {
              if (prev.some((message) => message.id === nextMessage.id)) return prev
              return [nextMessage, ...prev]
            })

            // The turn's answer: a screen capture shown live (AB#6762) gives way
            // to the persisted attachment the answer carries.
            if (
              (nextMessage.role === 'agent' && nextMessage.content?.type === 'text') ||
              nextMessage.role === 'error'
            ) {
              settleLiveCaptures(
                conversationId,
                String(nextMessage.id),
                Boolean(nextMessage.attachments?.some(isImageAttachment)),
              )
            }

            if (nextMessage.role === 'agent' && nextMessage.content?.type === 'text') {
              bufferRef.current = ''
              setLiveAgentText('')
              stopFlush()
            }

            if (nextMessage.role === 'tool') {
              setLiveToolUpdate(null)
            }

            if (atBottomRef.current) scrollToBottom()
            return
          }

          case 'tool.update':
            setLiveToolUpdate(event.payload as ToolSummaryContent)
            if (atBottomRef.current) scrollToBottom()
            return
          case 'attachment.indexing':
            if (atBottomRef.current) scrollToBottom()
            return

          // A stopped run's partial answer arrives as its own message; nothing
          // may keep streaming beside it (AB#6760).
          case 'run.stopped':
            bufferRef.current = ''
            setLiveAgentText('')
            stopFlush()
            setLiveToolUpdate(null)
            return
          default:
            return
        }
      },
    })

    return off
  }, [subscribe, queueFlush, stopFlush, scrollToBottom, conversationId])

  // `deliveryOf` too: a note appearing under the last bubble ("not sent", AB#6760)
  // grows the list without a new message, and must not end up below the fold.
  useEffect(() => {
    if (forceScrollToBottomRef.current) {
      forceScrollToBottomRef.current = false
      scrollListToBottom()
      return
    }
    if (atBottomRef.current) {
      scrollListToBottom()
    }
  }, [messages, liveAgentText, liveToolUpdate, indexingDocuments, deliveryOf, scrollListToBottom])

  const handleKbRefClick = useCallback((ref: {documentId: string; page?: number}) => {
    setKbPreview(ref)
  }, [])

  const sortedMessages = useMemo(
    () =>
      [...messages].sort((a, b) => new Date(a.created).getTime() - new Date(b.created).getTime()),
    [messages],
  )

  // Text of the most recent user turn — re-sent when retrying a failed turn.
  const lastUserText = useMemo(() => {
    for (let i = sortedMessages.length - 1; i >= 0; i--) {
      const m = sortedMessages[i]
      if (m.role === 'user' && m.content?.type === 'text') return m.content.text
    }
    return null
  }, [sortedMessages])

  if (loadingFirstPage) {
    return <ChatSkeleton label={t('chat_messages_loading')} />
  }

  if (isError && offset === 0 && !messages.length) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap={1}
        height="100%"
      >
        <Typography variant="body2" color="text.secondary">
          {t('chat_messages_load_error')}
        </Typography>
        <Button size="small" onClick={() => void refetch()}>
          {t('chat_access_retry')}
        </Button>
      </Box>
    )
  }

  if (!messages.length && !liveAgentText && !bufferRef.current) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
        <Typography variant="body2" color="text.secondary">
          {emptyStateText ?? t('chat_no_messages')}
        </Typography>
      </Box>
    )
  }

  return (
    <IndexingProvider documents={indexingDocuments}>
      <Box
        ref={scrollRef}
        onScroll={onScroll}
        sx={{height: '100%', overflowY: 'auto', overscrollBehavior: 'contain'}}
      >
        <Box display="flex" flexDirection="column" gap={0.5} px={0.5} pb={0.5}>
          <div ref={topRef} />

          {isFetching && offset > 0 ? (
            <Box display="flex" justifyContent="center" py={1}>
              <CircularProgress size={18} />
            </Box>
          ) : null}

          {sortedMessages.map((message, index) => (
            <ChatMessageRow
              key={message.id ?? `${message.role}-${index}-${message.created}`}
              message={message}
              messageIndex={index}
              conversationId={conversationId}
              tone={tone}
              showAuthors={showAuthors}
              currentUser={currentUser}
              onKbRefClick={handleKbRefClick}
              getActorLabel={getActorLabel}
              isActorLoading={isActorLoading}
              delivery={deliveryOf(message)}
              onRetry={
                onResend &&
                lastUserText &&
                index === sortedMessages.length - 1 &&
                message.role === 'error'
                  ? () => onResend(lastUserText)
                  : undefined
              }
              retrying={sending}
            />
          ))}

          {/* While the turn is held waiting for a file, that is what the agent
              is doing — and it is the only thing it is doing, so it stands in
              for the tool bubble rather than sitting beside it (AB#5992). */}
          {Object.keys(indexingDocuments).length > 0 ? (
            <WaitingBubble key="attachment-waiting" text={t('attachment_waiting')} tone={tone} />
          ) : liveToolUpdate ? (
            <ToolBubble key="live-tool-update" content={liveToolUpdate} final={false} tone={tone} />
          ) : null}

          {pendingCaptures.length > 0 ? (
            <Box
              data-testid="alpinist-live-screen-captures"
              sx={{display: 'flex', gap: 0.75, flexWrap: 'wrap', pl: 3.5}}
            >
              {pendingCaptures.map((capture) => (
                <ImageThumbnail
                  key={capture.requestId}
                  src={capture.url}
                  alt={t('screen_capture_alt')}
                />
              ))}
            </Box>
          ) : null}

          {liveAgentText || bufferRef.current ? (
            <AgentBubble key="live-agent" tone={tone}>
              <MarkdownText
                text={stripReasoningText(liveAgentText, true)}
                onKbRefClick={handleKbRefClick}
                streaming
              />
            </AgentBubble>
          ) : null}
        </Box>
      </Box>

      {/* A citation opens the document at what it cites: page 429 of a PDF as
          that page of the PDF, a sheet of a workbook as the passage read from
          it, since a browser cannot draw a workbook (AB#6057). */}
      <DocumentPreviewModal
        open={Boolean(kbPreview)}
        onClose={() => setKbPreview(null)}
        documentId={kbPreview?.documentId ?? null}
        title={kbPreview?.title}
        page={kbPreview?.page}
      />
    </IndexingProvider>
  )
}

export default memo(ChatMessageList)
